import { useEffect, useState } from "react";

import { Card } from "primereact/card";
import { InputSwitch } from "primereact/inputswitch";
import { Skeleton } from "primereact/skeleton";

import { getSvcSettingsClient } from "sj-ui-toolkit/src/client/svcSettings";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { useToast } from "sj-ui-toolkit/src/hooks";
import {
  COGNITO_AUTH_SETTINGS_GROUP,
  PASSWORD_AUTH_FIELD,
  fetchPasswordAuthEnabled,
} from "@/api/settings";

const PASSWORD_REQUIREMENTS = [
  "1 digit",
  "1 lowercase letter",
  "1 uppercase letter",
  "1 special character",
  "10 or more characters",
];

export const EmailPasswordAuthCard = () => {
  const currentTenantName = getCurrentSelectedTenant() ?? "";
  const { showToast } = useToast();

  const [enabled, setEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!currentTenantName) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const load = async () => {
      try {
        const passwordAuthEnabled = await fetchPasswordAuthEnabled(currentTenantName);
        if (!cancelled) setEnabled(passwordAuthEnabled);
      } catch (error) {
        console.error("Error loading email and password authentication setting:", error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [currentTenantName]);

  const handleChange = async (nextEnabled: boolean) => {
    if (!currentTenantName) return;

    const previousEnabled = enabled;
    setEnabled(nextEnabled);

    try {
      const { promise } = getSvcSettingsClient().updateTenantSettings(
        currentTenantName,
        COGNITO_AUTH_SETTINGS_GROUP,
        { [PASSWORD_AUTH_FIELD]: nextEnabled }
      );
      await promise;
      showToast({
        severity: "success",
        summary: "Success",
        detail: "Email and password authentication updated successfully",
      });
    } catch (error) {
      console.error("Error updating email and password authentication setting:", error);
      setEnabled(previousEnabled);
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Failed to update email and password authentication. Please try again.",
      });
    }
  };

  return (
    <Card className="mb-4">
      <div className="flex justify-between items-center mb-2">
        <h2 className="font-semibold text-lg">Email &amp; Password Authentication</h2>
        {isLoading ? (
          <Skeleton width="3rem" height="1.75rem" />
        ) : (
          <InputSwitch checked={enabled} onChange={e => handleChange(e.value)} />
        )}
      </div>

      <p className="body-sm">Users log in with their email and a password, which must contain:</p>
      <ul className="list-disc list-inside body-sm mt-1">
        {PASSWORD_REQUIREMENTS.map(requirement => (
          <li key={requirement}>{requirement}</li>
        ))}
      </ul>
    </Card>
  );
};
